import { useState, useCallback } from 'react';
import { Droplets, Check, Info, RotateCw } from 'lucide-react';
import LabBench from '../../LabBench';
import { Dropper, LitmusPaper } from '@/components/lab';
import InfoBox from '../../InfoBox';

const stepInfoContent: Record<number, { title: string; content: string }> = {
  1: {
    title: 'Preparing the Iodide Solution',
    content: `Iodide (I-) is the reduced form of iodine and can be oxidized to elemental iodine (I2). Fresh iodide solution appears colorless to pale yellow. The iodide ion is the largest common halide and is easily oxidized due to its low ionization energy. This oxidation reaction forms the basis of the iodine liberation test.`
  },
  2: {
    title: 'Acidifying with Acetic Acid',
    content: `Acetic acid (CH3COOH) provides the acidic environment needed for the oxidation reaction. We use acetic acid rather than strong mineral acids because it's gentler and won't cause unwanted side reactions. The acid provides H+ ions that are consumed in the redox reaction. Acidification is gradual - add dropwise until the solution tests acidic.`
  },
  3: {
    title: 'Testing pH with Litmus Paper',
    content: `Blue litmus paper turns red in acidic solution (pH < 7). This confirms sufficient acetic acid has been added. The color change occurs because litmus contains anthocyanin pigments that change structure based on H+ concentration. Testing pH ensures the oxidation reaction will proceed properly - nitrite oxidizes iodide only in acidic conditions.`
  },
  4: {
    title: 'Adding Potassium Nitrite Oxidant',
    content: `KNO₂ (potassium nitrite) acts as the oxidizing agent. In acidic solution, nitrite ion (NO₂⁻) oxidizes iodide to iodine: 2I⁻ + 2NO₂⁻ + 4H⁺ → I₂ + 2NO + 2H₂O. The nitrite is reduced to nitric oxide (NO) gas. This redox reaction is specific for iodide - other halides are not oxidized under these mild conditions.`
  },
  5: {
    title: 'Observing Iodine Liberation',
    content: `The appearance of a reddish-brown color indicates elemental iodine (I2) has formed. Iodine in aqueous solution forms triiodide complexes (I3-) that absorb blue light, appearing brown. The color intensity indicates the amount of iodide present. This color change is the first indication of a positive iodide test.`
  },
  6: {
    title: 'Adding Methylene Chloride',
    content: `Methylene chloride (CH₂Cl₂, dichloromethane) is an organic solvent used to extract iodine from the aqueous layer. I2 is more soluble in organic solvents than in water due to its non-polar nature. CH₂Cl₂ is denser than water, so it sinks to the bottom. The extraction provides a clearer color observation.`
  },
  7: {
    title: 'Shaking for Extraction',
    content: `Shaking thoroughly mixes the two phases, allowing iodine to transfer from the aqueous layer to the organic layer. The distribution follows the partition coefficient, which strongly favors the organic phase. After shaking, allow layers to separate - the denser CH₂Cl₂ settles to the bottom.`
  },
  8: {
    title: 'Observing the Violet Organic Layer',
    content: `Iodine dissolved in organic solvents appears violet rather than brown. This is because I2 in non-polar solvents exists as discrete molecules without triiodide formation, absorbing yellow-green light and appearing violet. The intense violet color in the bottom (organic) layer is definitive proof of iodine presence.`
  },
  9: {
    title: 'Confirming Iodide Presence',
    content: `A positive iodide test requires: (1) brown color in aqueous solution after oxidation, and (2) violet color in the organic layer after extraction. The two-step confirmation eliminates false positives. The oxidation-extraction sequence is specific for iodide - other halides don't produce the characteristic violet organic layer.`
  }
};

interface Part4IodideProps {
  onStepComplete?: (stepId: string) => void;
  currentStep?: number;
}

export default function Part4Iodide({ onStepComplete, currentStep = 1 }: Part4IodideProps) {
  const [showInfo, setShowInfo] = useState<number | null>(null);
  const [samplePrepared, setSamplePrepared] = useState(false);
  const [aceticAcidAdded, setAceticAcidAdded] = useState(false);
  const [isDropping, setIsDropping] = useState(false);
  const [pHTested, setPHTested] = useState(false);
  const [litmusColor, setLitmusColor] = useState<'red' | 'blue'>('blue');
  const [kno2Added, setKno2Added] = useState(false);
  const [i2Liberated, setI2Liberated] = useState(false);
  const [ch2cl2Added, setCh2cl2Added] = useState(false);
  const [shaken, setShaken] = useState(false);
  const [organicLayerObserved, setOrganicLayerObserved] = useState(false);
  const [resultRecorded, setResultRecorded] = useState(false);

  // Colors for the iodine test
  const getAqueousColor = () => {
    if (!samplePrepared) return 'transparent';
    if (!kno2Added) return '#FFFDE7'; // Pale yellow iodide solution
    if (!i2Liberated) return '#A1887F'; // Reddish-brown I2 forming
    return '#8D6E63'; // Reddish-brown I2 in water
  };

  const getOrganicColor = () => {
    if (!shaken) return '#F5F5F5'; // Colorless CH₂Cl₂
    return '#7B1FA2'; // Violet I2 in organic layer
  };

  const handlePrepareSample = useCallback(() => {
    setSamplePrepared(true);
    if (onStepComplete) onStepComplete('iodide-1');
  }, [onStepComplete]);

  const handleAddAceticAcid = useCallback(() => {
    if (samplePrepared) {
      setIsDropping(true);
      setTimeout(() => {
        setIsDropping(false);
        setAceticAcidAdded(true);
        if (onStepComplete) onStepComplete('iodide-2');
      }, 500);
    }
  }, [samplePrepared, onStepComplete]);

  const handleTestpH = useCallback(() => {
    if (aceticAcidAdded) {
      setLitmusColor('red');
      setPHTested(true);
      if (onStepComplete) onStepComplete('iodide-3');
    }
  }, [aceticAcidAdded, onStepComplete]);

  const handleAddKNO2 = useCallback(() => {
    if (pHTested) {
      setIsDropping(true);
      setTimeout(() => {
        setIsDropping(false);
        setKno2Added(true);
        if (onStepComplete) onStepComplete('iodide-4');
      }, 500);
    }
  }, [pHTested, onStepComplete]);

  const handleObserveI2 = useCallback(() => {
    if (kno2Added) {
      setI2Liberated(true);
      if (onStepComplete) onStepComplete('iodide-5');
    }
  }, [kno2Added, onStepComplete]);

  const handleAddCH2Cl2 = useCallback(() => {
    if (i2Liberated) {
      setIsDropping(true);
      setTimeout(() => {
        setIsDropping(false);
        setCh2cl2Added(true);
        if (onStepComplete) onStepComplete('iodide-6');
      }, 500);
    }
  }, [i2Liberated, onStepComplete]);

  const handleShake = useCallback(() => {
    if (ch2cl2Added) {
      setShaken(true);
      if (onStepComplete) onStepComplete('iodide-7');
    }
  }, [ch2cl2Added, onStepComplete]);

  const handleObserveOrganic = useCallback(() => {
    if (shaken) {
      setOrganicLayerObserved(true);
      if (onStepComplete) onStepComplete('iodide-8');
    }
  }, [shaken, onStepComplete]);

  const handleRecordResult = useCallback(() => {
    if (organicLayerObserved) {
      setResultRecorded(true);
      if (onStepComplete) onStepComplete('iodide-9');
    }
  }, [organicLayerObserved, onStepComplete]);

  return (
    <LabBench
      experimentName="Iodide Liberation Test"
      completedCount={[samplePrepared, aceticAcidAdded, pHTested, kno2Added, i2Liberated, ch2cl2Added, shaken, organicLayerObserved, resultRecorded].filter(Boolean).length}
      totalSteps={9}
      equipment={['I- solution', '6M Acetic acid', '1M KNO2', 'Methylene chloride', 'Litmus paper']}
      labMode="guided"
      currentStep={currentStep}
    >
      <div className="iodide-test">
        {/* Equipment Area */}
        <div className="equipment-row">
          {/* Test Tube with layers */}
          <div className="equipment-section">
            <svg viewBox="0 0 70 160" width={70} height={160}>
              <defs>
                <linearGradient id="tube-glass-iodide" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="rgba(200,220,240,0.6)" />
                  <stop offset="50%" stopColor="rgba(255,255,255,0.3)" />
                  <stop offset="100%" stopColor="rgba(200,220,240,0.5)" />
                </linearGradient>
                <linearGradient id="aqueous-grad" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor={getAqueousColor()} stopOpacity="0.85" />
                  <stop offset="100%" stopColor={getAqueousColor()} stopOpacity="0.95" />
                </linearGradient>
                <linearGradient id="organic-grad" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor={getOrganicColor()} stopOpacity="0.85" />
                  <stop offset="100%" stopColor={getOrganicColor()} stopOpacity="0.95" />
                </linearGradient>
              </defs>

              {/* Test tube rim */}
              <ellipse cx="35" cy="15" rx="16" ry="5" fill="rgba(200,220,240,0.5)" stroke="#8BA8C4" strokeWidth="1.5" />

              {/* Test tube body */}
              <path d="M19 15 L19 125 Q19 140 35 140 Q51 140 51 125 L51 15" fill="url(#tube-glass-iodide)" stroke="#8BA8C4" strokeWidth="1.5" />

              {/* Aqueous layer (top) */}
              {samplePrepared && (
                <path
                  d={`M21 ${ch2cl2Added ? 45 : 40} L21 ${ch2cl2Added ? 95 : 123} Q21 ${ch2cl2Added ? 95 : 137} 35 ${ch2cl2Added ? 95 : 137} Q49 ${ch2cl2Added ? 95 : 137} 49 ${ch2cl2Added ? 95 : 123} L49 ${ch2cl2Added ? 45 : 40} Z`}
                  fill="url(#aqueous-grad)"
                />
              )}

              {/* Interface line when two layers present */}
              {ch2cl2Added && (
                <line x1="21" y1="95" x2="49" y2="95" stroke="rgba(0,0,0,0.2)" strokeWidth="1" strokeDasharray="2,2" />
              )}

              {/* Organic layer (bottom) - denser */}
              {ch2cl2Added && (
                <path
                  d="M21 95 L21 123 Q21 137 35 137 Q49 137 49 123 L49 95 Z"
                  fill="url(#organic-grad)"
                >
                  {shaken && (
                    <animate
                      attributeName="opacity"
                      values="0.8;0.95;0.8"
                      dur="2s"
                      repeatCount="indefinite"
                    />
                  )}
                </path>
              )}

              {/* Bubble animation during I2 liberation */}
              {kno2Added && !i2Liberated && (
                <g>
                  {[...Array(5)].map((_, i) => (
                    <circle
                      key={i}
                      cx={25 + i * 5}
                      cy={100}
                      r={1.5}
                      fill="rgba(255,255,255,0.6)"
                    >
                      <animate
                        attributeName="cy"
                        values="100;70;50"
                        dur={`${0.8 + i * 0.15}s`}
                        repeatCount="indefinite"
                      />
                      <animate
                        attributeName="opacity"
                        values="0.6;0.3;0"
                        dur={`${0.8 + i * 0.15}s`}
                        repeatCount="indefinite"
                      />
                    </circle>
                  ))}
                </g>
              )}

              {/* Layer labels */}
              {ch2cl2Added && (
                <g fontSize="8" fill="var(--text-muted)">
                  <text x="55" y="70" textAnchor="start">Aqueous</text>
                  <text x="55" y="115" textAnchor="start">Organic</text>
                </g>
              )}

              {/* Glass shine */}
              <line x1="23" y1="20" x2="23" y2="115" stroke="rgba(255,255,255,0.5)" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <span className="equipment-label">I- Solution</span>
          </div>

          {/* Acetic Acid Dropper */}
          <div className="equipment-section">
            <Dropper
              label="6M Acetic Acid"
              liquidColor="#F5F5F5"
              onClick={samplePrepared && !aceticAcidAdded ? handleAddAceticAcid : undefined}
              active={samplePrepared && !aceticAcidAdded}
              dropping={isDropping && !aceticAcidAdded}
              disabled={!samplePrepared || aceticAcidAdded}
            />
          </div>

          {/* Litmus Paper */}
          <div className="equipment-section">
            <LitmusPaper
              color={litmusColor}
              onClick={aceticAcidAdded && !pHTested ? handleTestpH : undefined}
              active={aceticAcidAdded && !pHTested}
              disabled={!aceticAcidAdded || pHTested}
            />
          </div>

          {/* KNO2 Dropper */}
          {pHTested && !kno2Added && (
            <div className="equipment-section">
              <Dropper
                label="1M KNO2"
                liquidColor="#F5F5F5"
                onClick={handleAddKNO2}
                active={true}
                dropping={isDropping}
              />
            </div>
          )}

          {/* CH₂Cl₂ Dropper */}
          {i2Liberated && !ch2cl2Added && (
            <div className="equipment-section">
              <Dropper
                label="CH₂Cl₂"
                liquidColor="#F5F5F5"
                onClick={handleAddCH2Cl2}
                active={true}
                dropping={isDropping}
              />
            </div>
          )}
        </div>

        {/* Color Indicator */}
        {kno2Added && (
          <div className="color-indicator">
            <div className="color-section">
              <span>Aqueous Layer:</span>
              <div className="color-swatch" style={{ backgroundColor: getAqueousColor() }} />
              <span className="color-desc">
                {!i2Liberated ? 'Reddish-brown forming...' : 'Reddish-brown (I2)'}
              </span>
            </div>
            {shaken && (
              <div className="color-section">
                <span>Organic Layer:</span>
                <div className="color-swatch" style={{ backgroundColor: getOrganicColor() }} />
                <span className="color-desc violet">Violet (I2 extracted)</span>
              </div>
            )}
          </div>
        )}

        {/* Status Messages */}
        <div className="status-section">
          {!samplePrepared && (
            <div className="instruction-card">
              <span className="step-badge">Step 1</span>
              <p>Add 5 mL of fresh <strong>I- solution</strong> to a test tube.</p>
              <button className="action-btn" onClick={handlePrepareSample}>
                <Droplets size={16} />
                Prepare I- Solution
              </button>
            </div>
          )}

          {samplePrepared && !aceticAcidAdded && (
            <div className="instruction-card">
              <span className="step-badge">Step 2</span>
              <p>Acidify with <strong>6M acetic acid</strong> dropwise until acidic.</p>
              <p className="hint">Click the acetic acid dropper.</p>
            </div>
          )}

          {aceticAcidAdded && !pHTested && (
            <div className="instruction-card">
              <span className="step-badge">Step 3</span>
              <p>Test pH with <strong>blue litmus paper</strong> to confirm acidic.</p>
              <p className="hint">Click the litmus paper to test.</p>
            </div>
          )}

          {pHTested && !kno2Added && (
            <div className="instruction-card">
              <span className="step-badge">Step 4</span>
              <p>Add <strong>2 drops of 1M potassium nitrite (KNO2)</strong>.</p>
              <p className="hint">Click the KNO2 dropper.</p>
            </div>
          )}

          {kno2Added && !i2Liberated && (
            <div className="instruction-card">
              <span className="step-badge">Step 5</span>
              <p>Observe the <strong>reddish-brown color</strong> of liberated I2.</p>
              <button className="action-btn" onClick={handleObserveI2}>
                <Check size={16} />
                Observe I2 Liberation
              </button>
            </div>
          )}

          {i2Liberated && !ch2cl2Added && (
            <div className="instruction-card">
              <span className="step-badge">Step 6</span>
              <p>Add <strong>15 drops of methylene chloride (CH₂Cl₂)</strong>.</p>
              <p className="hint">Click the CH₂Cl₂ dropper.</p>
            </div>
          )}

          {ch2cl2Added && !shaken && (
            <div className="instruction-card">
              <span className="step-badge">Step 7</span>
              <p><strong>Shake</strong> the tube and allow layers to separate.</p>
              <button className="action-btn" onClick={handleShake}>
                <RotateCw size={16} />
                Shake Tube
              </button>
            </div>
          )}

          {shaken && !organicLayerObserved && (
            <div className="instruction-card">
              <span className="step-badge">Step 8</span>
              <p>Observe the <strong>violet color in the bottom (organic) layer</strong>.</p>
              <button className="action-btn" onClick={handleObserveOrganic}>
                <Check size={16} />
                Observe Organic Layer
              </button>
            </div>
          )}

          {organicLayerObserved && !resultRecorded && (
            <div className="instruction-card success">
              <span className="step-badge">Step 9</span>
              <p>Record the <strong>positive result</strong> for iodide confirmation.</p>
              <button className="action-btn success" onClick={handleRecordResult}>
                <Check size={16} />
                Record Result
              </button>
            </div>
          )}

          {resultRecorded && (
            <div className="result-card">
              <Check size={20} />
              <div>
                <p><strong>I- Confirmed!</strong></p>
                <p>The violet color in the organic layer confirms iodine was liberated and extracted.</p>
              </div>
            </div>
          )}
        </div>

        {/* pH indicator */}
        {pHTested && (
          <div className="ph-indicator">
            <span>pH Test:</span>
            <span className={`ph-result ${litmusColor}`}>
              Litmus turned {litmusColor} - Solution is ACIDIC
            </span>
          </div>
        )}

        {/* Theory Box */}
        <div className="theory-box">
          <h4><Info size={14} /> Iodine Liberation Test</h4>
          <p>
            When iodide ions are oxidized by nitrite in acidic solution, elemental iodine (I2)
            is liberated, causing a reddish-brown color. Iodine is more soluble in organic
            solvents like methylene chloride, producing a characteristic <strong>violet color</strong>.
          </p>
          <div className="equation">
            2I<sup>-</sup> + 2NO<sub>2</sub><sup>-</sup> + 4H<sup>+</sup> → I<sub>2</sub> + 2NO + 2H<sub>2</sub>O
          </div>
          <p className="equation-note">I2 is violet in organic solvents, reddish-brown in water.</p>
        </div>

        <style>{`
          .iodide-test {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            padding: 20px;
            width: 100%;
            max-width: 700px;
          }

          .equipment-row {
            display: flex;
            gap: 30px;
            align-items: flex-end;
            flex-wrap: wrap;
            justify-content: center;
          }

          .equipment-section {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
          }

          .equipment-label {
            font-size: 11px;
            color: var(--text-secondary);
          }

          .color-indicator {
            display: flex;
            flex-direction: column;
            gap: 10px;
            padding: 12px 20px;
            background: var(--bg-elevated);
            border-radius: 8px;
            width: 100%;
          }

          .color-section {
            display: flex;
            align-items: center;
            gap: 12px;
            font-size: 12px;
          }

          .color-swatch {
            width: 24px;
            height: 24px;
            border-radius: 50%;
            border: 2px solid var(--border);
          }

          .color-desc {
            color: var(--text-secondary);
          }

          .color-desc.violet {
            color: #7B1FA2;
            font-weight: 600;
          }

          .status-section {
            width: 100%;
            min-height: 100px;
          }

          .instruction-card {
            background: var(--bg-elevated);
            padding: 16px 20px;
            border-radius: 12px;
            border-left: 4px solid var(--primary);
          }

          .instruction-card.success {
            border-left-color: #00A86B;
          }

          .step-badge {
            display: inline-block;
            background: var(--primary);
            color: white;
            padding: 4px 10px;
            border-radius: 12px;
            font-size: 11px;
            font-weight: 600;
            margin-bottom: 8px;
          }

          .instruction-card p {
            margin: 0 0 8px 0;
            font-size: 13px;
            color: var(--text-primary);
          }

          .instruction-card .hint {
            font-size: 12px;
            color: var(--text-muted);
            font-style: italic;
          }

          .action-btn {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 10px 20px;
            background: var(--primary);
            color: white;
            border: none;
            border-radius: 8px;
            font-size: 13px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s;
            margin-top: 8px;
          }

          .action-btn:hover {
            background: var(--primary-dark);
            transform: translateY(-2px);
          }

          .action-btn.success {
            background: #00A86B;
          }

          .action-btn.success:hover {
            background: #008B5B;
          }

          .result-card {
            display: flex;
            align-items: flex-start;
            gap: 12px;
            padding: 16px 20px;
            background: rgba(0, 168, 107, 0.1);
            border-radius: 12px;
            color: #00A86B;
          }

          .result-card p {
            margin: 0;
            color: var(--text-primary);
          }

          .result-card p:first-child {
            color: #00A86B;
            font-weight: 600;
          }

          .ph-indicator {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 10px 16px;
            background: var(--bg-elevated);
            border-radius: 8px;
            font-size: 13px;
          }

          .ph-result {
            font-weight: 600;
          }

          .ph-result.red {
            color: #DC2626;
          }

          .theory-box {
            width: 100%;
            background: var(--bg-elevated);
            padding: 16px 20px;
            border-radius: 12px;
          }

          .theory-box h4 {
            display: flex;
            align-items: center;
            gap: 8px;
            margin: 0 0 8px 0;
            font-size: 13px;
            color: var(--text-secondary);
          }

          .theory-box p {
            margin: 0 0 12px 0;
            font-size: 12px;
            color: var(--text-primary);
            line-height: 1.6;
          }

          .theory-box .equation {
            text-align: center;
            font-size: 13px;
            font-family: 'Times New Roman', serif;
            color: var(--text-secondary);
            background: var(--bg-dark);
            padding: 10px;
            border-radius: 6px;
            margin-bottom: 8px;
          }

          .equation-note {
            font-size: 11px !important;
            color: var(--text-muted) !important;
            text-align: center;
          }

          .info-buttons-row {
            display: flex;
            gap: 8px;
            justify-content: center;
            flex-wrap: wrap;
          }

          .step-info-btn {
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: var(--accent);
            color: var(--bg-dark);
            font-size: 11px;
            font-weight: 700;
            border: none;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            transition: transform 0.15s;
          }
          .step-info-btn:hover {
            transform: scale(1.1);
          }
        `}</style>

        {/* Info Buttons */}
        <div className="info-buttons-row">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((stepId) => (
            stepInfoContent[stepId] && (
              <button
                key={stepId}
                className="step-info-btn"
                onClick={(e) => { e.stopPropagation(); setShowInfo(stepId); }}
                title={stepInfoContent[stepId].title}
              >
                ?
              </button>
            )
          ))}
        </div>

        {showInfo && stepInfoContent[showInfo] && (
          <InfoBox
            step={showInfo}
            experimentId="exp3"
            stepInfo={stepInfoContent[showInfo]}
            onClose={() => setShowInfo(null)}
          />
        )}
      </div>
    </LabBench>
  );
}
